/**
 * DL-01 — the slice of the File System Access API the download writes with,
 * as structural types (a test double satisfies them, the DI-5-2 pattern
 * `skyspecsExport/writer.ts:ExportDirLike`), and the picker.
 *
 * Chromium only (Chrome / Edge): Firefox and Safari have no
 * `showDirectoryPicker` — the dialog refuses BEFORE anything is asked
 * (`supportsFolderDownload`), with the CLI as the way out. The picker is
 * read-write and must be called from a user gesture; its own id so the
 * browser opens it where the last download went.
 */

export interface WritableLike {
  write(data: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort?(): Promise<void>;
}

export interface FileLike {
  readonly name: string;
  getFile(): Promise<Blob>;
  createWritable(opts?: { keepExistingData?: boolean }): Promise<WritableLike>;
}

export interface DownloadDirLike {
  readonly name: string;
  getDirectoryHandle(
    name: string,
    opts?: { create?: boolean },
  ): Promise<DownloadDirLike>;
  getFileHandle(name: string, opts?: { create?: boolean }): Promise<FileLike>;
  removeEntry?(name: string, opts?: { recursive?: boolean }): Promise<void>;
}

type PickerWindow = Window & {
  showDirectoryPicker?: (opts?: {
    mode?: "read" | "readwrite";
    id?: string;
  }) => Promise<DownloadDirLike>;
};

export const DOWNLOAD_PICKER_ID = "aeriosiq-image-download";

/** A feature probe — never prompts, never opens a picker. */
export function supportsFolderDownload(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (window as PickerWindow).showDirectoryPicker === "function"
  );
}

/**
 * The folder the images go into (read-write). Null when dismissed or when
 * this browser has no picker. Must be called from a user gesture.
 */
export async function pickDownloadRoot(): Promise<DownloadDirLike | null> {
  const w = window as PickerWindow;
  if (!w.showDirectoryPicker) return null;
  try {
    return await w.showDirectoryPicker({
      mode: "readwrite",
      id: DOWNLOAD_PICKER_ID,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") return null;
    throw e;
  }
}

/** The existing file of that name, or null (not there / not readable). */
export async function existingFileOf(
  dir: DownloadDirLike,
  name: string,
): Promise<Blob | null> {
  try {
    const h = await dir.getFileHandle(name);
    return await h.getFile();
  } catch {
    return null;
  }
}

/** Best-effort delete of a partial / wrong file; never throws. */
export async function discardFile(
  dir: DownloadDirLike,
  name: string,
): Promise<void> {
  if (!dir.removeEntry) return;
  try {
    await dir.removeEntry(name);
  } catch {
    // a file that is not there any more, or a browser without removeEntry
  }
}
